import type { KeenSliderOptions } from 'keen-slider/react'
import { useKeenSlider } from 'keen-slider/react'
import { useEffect, useState } from 'react'

import { type CarouselProps } from './CarouselProps'

export function useCarouselHandlers(args: CarouselProps) {
  const {
    initialSlide = 0,
    slides,
    slidesPerView = 3,
    autoSlide = false,
    autoSlideInterval = 2000,
    overflowDesktop,
    overflowMobile = true,
    numberOfSlidesMoving = 3,
    alignmentDesktop = 'center',
    alignmentMobile = 'center',
    slideSpacing = 40,
    animationDuration = 1000,
  } = args
  const [currentSlide, setCurrentSlide] = useState(initialSlide)
  const [isLoaded, setLoaded] = useState(false)
  const slideCount = typeof window !== 'undefined' && window?.innerWidth <= 720 ? 3 : slidesPerView

  const keenSliderOptions: KeenSliderOptions = {
    initial: initialSlide,
    slides: {
      perView: overflowDesktop ? slideCount - 1 : slideCount,
      spacing: slideSpacing,
      origin: alignmentDesktop,
    },
    loop: true,
    defaultAnimation: {
      duration: animationDuration,
    },
    slideChanged(slider) {
      setCurrentSlide(slider.track.details.rel)
    },
    created() {
      setLoaded(true)
    },
    breakpoints: {
      '(max-width: 768px)': {
        slides: {
          perView: overflowMobile ? 2 : 1,
          spacing: 25,
          origin: alignmentMobile,
        },
      },
    },
  }

  const [sliderRef, instanceRef] = useKeenSlider(keenSliderOptions, [])

  useEffect(() => {
    if (!autoSlide || !instanceRef.current) return
    let timeout: NodeJS.Timeout
    let mouseOver = false
    function clearNextTimeout() {
      clearTimeout(timeout)
    }
    function nextTimeout() {
      clearTimeout(timeout)
      if (mouseOver) return
      timeout = setTimeout(() => {
        instanceRef?.current?.next()
      }, autoSlideInterval)
    }

    instanceRef?.current?.container.addEventListener('mouseover', () => {
      mouseOver = true
      clearNextTimeout()
    })
    instanceRef?.current?.container.addEventListener('mouseout', () => {
      mouseOver = false
      nextTimeout()
    })
    nextTimeout()
    instanceRef?.current?.on('dragStarted', clearNextTimeout)
    instanceRef?.current?.on('animationEnded', nextTimeout)
    instanceRef?.current?.on('updated', nextTimeout)
  }, [autoSlide, autoSlideInterval, instanceRef])

  function previousSlide() {
    const newIndex = (currentSlide - numberOfSlidesMoving + slides.length) % slides.length
    instanceRef.current?.moveToIdx(newIndex)
  }

  function nextSlide() {
    const newIndex = (currentSlide + numberOfSlidesMoving) % slides.length
    instanceRef.current?.moveToIdx(newIndex)
  }

  function moveOnKeyPress(event: any) {
    if (event.keyCode === 37) {
      previousSlide()
    } else if (event.keyCode === 39) {
      nextSlide()
    }
  }

  return {
    sliderRef,
    moveOnKeyPress,
    isLoaded,
    instanceRef,
    previousSlide,
    nextSlide,
    currentSlide,
    keenSliderOptions,
  }
}
